// Product-unit conventions used throughout the app for SKUs, production
// schedules and batch outputs. All finished-goods work in ONE of these
// three units — no other unit is allowed on those forms.
//
// Conversion:
//   1 sheet = 12 packets
//   1 box   = 16 sheets = 192 packets
//
// Helpers here keep the logic in one place so we can add / rename units
// without touching every screen.

export const PACKETS_PER_SHEET = 12;
export const SHEETS_PER_BOX = 16;
export const PACKETS_PER_BOX = PACKETS_PER_SHEET * SHEETS_PER_BOX; // 192

export const PRODUCT_UNITS = ["packets", "sheet", "box"];

// Normalise a quantity into the "packets" base unit. Anything else (kg, nos,
// pcs, blank …) returns null so callers can decide to skip conversion.
export function toPackets(qty, unit) {
  const q = parseFloat(qty);
  if (!Number.isFinite(q) || q === 0) return 0;
  const u = (unit || "").trim().toLowerCase();
  if (u === "packets" || u === "packet" || u === "pack" || u === "packs") return q;
  if (u === "sheet" || u === "sheets") return q * PACKETS_PER_SHEET;
  if (u === "box" || u === "boxes") return q * PACKETS_PER_BOX;
  return null;
}

// Break a total-packet count into "X box · Y sheet · Z packets", skipping
// zero segments and keeping remainders exact (no rounding). Returns "" when
// count is 0 or invalid.
export function formatBoxSheetPackets(packetsTotal) {
  const n = Number(packetsTotal);
  if (!Number.isFinite(n) || n <= 0) return "";
  let remaining = n;
  const boxes = Math.floor(remaining / PACKETS_PER_BOX);
  remaining -= boxes * PACKETS_PER_BOX;
  const sheets = Math.floor(remaining / PACKETS_PER_SHEET);
  remaining -= sheets * PACKETS_PER_SHEET;
  const packets = remaining;
  const parts = [];
  if (boxes)   parts.push(`${boxes} box`);
  if (sheets)  parts.push(`${sheets} sheet`);
  // Show packets even when it's a fractional remainder so we don't lose it.
  if (packets) parts.push(`${Number.isInteger(packets) ? packets : packets.toFixed(2)} packets`);
  return parts.join(" · ");
}

// Combine a `doneByUnit` map (e.g. {packets: 200, sheet: 3}) into a total
// packet count, ignoring any units outside the 3-unit convention.
export function totalPackets(doneByUnit) {
  if (!doneByUnit) return 0;
  let total = 0;
  for (const [u, q] of Object.entries(doneByUnit)) {
    const p = toPackets(q, u);
    if (p != null) total += p;
  }
  return total;
}
